import React, { useCallback, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import Button from 'components/DesignSystem/Button';
import Icon from 'components/DesignSystem/Icon';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import BodyBold from 'components/Typography/BodyBold';
import BodyRegular from 'components/Typography/BodyRegular';
import OverlineSmall from 'components/Typography/OverlineSmall';
import ProgressoAprovacao from 'components/Relatorios/shared/Aprovacao/ProgressoAprovacao';
import PublicarModal from 'components/Relatorios/shared/Aprovacao/PublicarModal';
import useRelatorioAprovacao from 'hooks/Relatorios/Aprovacao/useRelatorioAprovacao';
import {
  descreverDesatualizado, descreverProcessamento, formatarDataHora, labelDaVariante,
  rotuloDaDataDoInforme, varianteDoStatus,
} from 'helper/Relatorios/Aprovacao';
import { cssVar } from 'constants/theme';

import './index.scss';

/**
 * Barra fixa acima do `Outlet` do informe, visível apenas para admin
 * em `modoRevisao` (Q8: dia `sem_informe` não renderiza nada). Estados:
 * loading, erro, "em revisão" (progresso + link para o painel + publicar) e
 * "publicado" (faixa verde + reabrir). Nos dois, abaixo do título, a linha
 * "Estudo <ds_name> processado em …" (`descreverProcessamento`) diz quando o Core
 * processou o estudo do release — só quando o backend manda o dado (DESSEM). Em revisão, se o
 * Core já tem um estudo mais novo e o release está preso pelas aprovações
 * (`simulacoes.desatualizado`), um aviso pede para revogar (`descreverDesatualizado`).
 */
export default function RevisaoBar() {
  const {
    config, modoRevisao, status, isLoading, isError, refetch,
    aprovadas, total, percentual, podePublicar, publicado,
    reabrir, mutando, erroMutacao,
  } = useRelatorioAprovacao();
  const [modalAberto, setModalAberto] = useState(false);

  const handleReabrir = useCallback(() => {
    reabrir().catch(() => undefined);
  }, [reabrir]);

  if (!modoRevisao) return null;

  if (isLoading && !status) {
    return (
      <div className="relatorio-aprovacao-revisao-bar relatorio-aprovacao-revisao-bar--carregando">
        <Spinner animation="border" role="status" size="sm" />
      </div>
    );
  }

  if (isError) {
    return (
      <QueryErrorState
        className="relatorio-aprovacao-revisao-bar__erro"
        title="Não foi possível carregar o status de aprovação"
        onRetry={refetch}
      />
    );
  }

  if (!status || status.status === 'sem_informe') return null;

  const dataBR = rotuloDaDataDoInforme(config, status.data_rodada);
  const cenarioLabel = labelDaVariante(config, varianteDoStatus(config, status));
  const processamento = descreverProcessamento(status);
  const desatualizado = descreverDesatualizado(status);

  if (publicado) {
    return (
      <div className="relatorio-aprovacao-revisao-bar relatorio-aprovacao-revisao-bar--publicado">
        <div className="relatorio-aprovacao-revisao-bar__info">
          <span className="relatorio-aprovacao-revisao-bar__icone" aria-hidden="true">
            <Icon iconType="checkmark" size="small" color={cssVar('text-on-accent')} />
          </span>
          <div className="relatorio-aprovacao-revisao-bar__textos">
            <BodyBold>{`Informe de ${dataBR} publicado para os clientes`}</BodyBold>
            {status.publicacao && (
              <BodyRegular>
                {`Publicado por ${status.publicacao.por} em ${formatarDataHora(status.publicacao.em)} · ${config.variante.rotulo} ${cenarioLabel}`}
              </BodyRegular>
            )}
            {processamento !== null && <BodyRegular>{processamento}</BodyRegular>}
          </div>
        </div>
        <Button
          type="secondary"
          className="relatorio-aprovacao-revisao-bar__reabrir"
          onClick={handleReabrir}
          disabled={mutando}
          small
        >
          Reabrir para edição
        </Button>
        {erroMutacao !== undefined && (
          <BodyRegular>Não foi possível reabrir o informe. Tente novamente.</BodyRegular>
        )}
      </div>
    );
  }

  return (
    <div className="relatorio-aprovacao-revisao-bar relatorio-aprovacao-revisao-bar--em-revisao">
      <div className="relatorio-aprovacao-revisao-bar__cabecalho">
        <OverlineSmall>MODO REVISÃO · ADMIN</OverlineSmall>
        <BodyBold>{`${config.titulo} · ${dataBR} · ${cenarioLabel}`}</BodyBold>
        {processamento !== null && <BodyRegular>{processamento}</BodyRegular>}
        {desatualizado !== null && (
          <div className="relatorio-aprovacao-revisao-bar__aviso" role="status">
            <Icon iconType="warning" size="small" />
            <BodyRegular>{desatualizado}</BodyRegular>
          </div>
        )}
      </div>
      <ProgressoAprovacao aprovadas={aprovadas} total={total} percentual={percentual} />
      <Link to={`${config.base}/aprovacao`} className="relatorio-aprovacao-revisao-bar__link">Painel de aprovação</Link>
      <Button
        className="relatorio-aprovacao-revisao-bar__publicar"
        onClick={() => setModalAberto(true)}
        disabled={!podePublicar}
        small
      >
        Publicar para clientes
      </Button>
      <PublicarModal open={modalAberto} onClose={() => setModalAberto(false)} />
    </div>
  );
}
